import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Card, Grid, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const statusOptions = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
];

const statusLabel = (status) =>
  ({ open: "Open", in_progress: "In Progress", resolved: "Resolved" }[status] ||
    status ||
    "Open");

const statusAppearance = (status) =>
  ({
    open: { background: "#fff7ed", color: "#b45309", border: "#fed7aa" },
    in_progress: { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    resolved: { background: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  }[status] || { background: "#f8fafc", color: "#475569", border: "#cbd5e1" });

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const imageUrl = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `${Constants.imageDomain}${value}`;
};

const lightFieldSx = {
  "& .MuiInputBase-root": { backgroundColor: "#ffffff", color: "#172b4d" },
  "& .MuiInputBase-input": { color: "#172b4d", WebkitTextFillColor: "#172b4d" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94a3b8",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#2563eb",
    borderWidth: "2px",
  },
};

function SupportTicketReply() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const messagesRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState(false);
  const [notice, setNotice] = useState("");
  const [noticeColor, setNoticeColor] = useState("success");

  const replyPreview = useMemo(
    () => (replyImage ? URL.createObjectURL(replyImage) : ""),
    [replyImage]
  );

  const visibleMessages = useMemo(
    () =>
      messages.filter((item, index) => {
        if (!String(item.legacy_key || "").endsWith(":initial")) return true;
        return !messages.some((candidate, candidateIndex) => {
          if (candidateIndex === index || candidate.legacy_key) return false;
          const timeDifference = Math.abs(
            new Date(candidate.createdAt).getTime() - new Date(item.createdAt).getTime()
          );
          return (
            candidate.sender_type === item.sender_type &&
            String(candidate.message || "") === String(item.message || "") &&
            String(candidate.image?.url || "") === String(item.image?.url || "") &&
            timeDifference <= 5 * 60 * 1000
          );
        });
      }),
    [messages]
  );

  const showNotice = useCallback((content, color = "success") => {
    setNotice(content);
    setNoticeColor(color);
    setNotification(true);
  }, []);

  const ensureAuth = useCallback(() => {
    if (init() === "success") return true;
    window.location.assign("/signin");
    return false;
  }, []);

  const loadTicket = useCallback(
    async ({ silent = false } = {}) => {
      if (!ticketId || !ensureAuth()) return;
      if (!silent) setLoading(true);
      try {
        const response = await axios.get(
          `${Constants.getUrls.getSupportTicketDetails}${ticketId}`
        );
        setTicket(response?.data?.data?.ticket || null);
        setMessages(response?.data?.data?.messages || []);
      } catch (error) {
        showNotice(error?.response?.data?.message || "Unable to open support ticket.", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [ensureAuth, showNotice, ticketId]
  );

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  useEffect(
    () => () => {
      if (replyPreview) URL.revokeObjectURL(replyPreview);
    },
    [replyPreview]
  );

  useEffect(() => {
    const element = messagesRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [visibleMessages.length]);

  const syncConversation = async () => {
    if (syncing) return;
    setSyncing(true);
    await loadTicket({ silent: true });
    setSyncing(false);
    showNotice("Conversation synced successfully.");
  };

  const selectReplyImage = (file) => {
    if (!file) {
      setReplyImage(null);
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      showNotice("Only JPG, PNG and WEBP images are allowed.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotice("Support image cannot exceed 5 MB.", "error");
      return;
    }
    setReplyImage(file);
  };

  const sendReply = async () => {
    if ((!reply.trim() && !replyImage) || sending || ticket?.support_status === "resolved") return;
    setSending(true);
    try {
      const identifier = ticket.ticket_id || ticket._id;
      const formData = new FormData();
      formData.append("message", reply.trim());
      if (replyImage) formData.append("image", replyImage);
      await axios.post(
        `${Constants.postUrls.sendSupportTicketMessage}${identifier}/messages`,
        formData
      );
      setReply("");
      setReplyImage(null);
      await loadTicket({ silent: true });
      showNotice("Reply sent successfully.");
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to send reply.", "error");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (nextStatus) => {
    if (!ticket || updating || ticket.support_status === nextStatus) return;
    if (
      nextStatus === "resolved" &&
      !window.confirm(
        "Mark this ticket as resolved? No further messages can be sent after resolution."
      )
    ) {
      return;
    }
    setUpdating(true);
    try {
      const identifier = ticket.ticket_id || ticket._id;
      await axios.patch(
        `${Constants.putUrls.updateSupportIssueStatus}${identifier}/status`,
        { status: nextStatus }
      );
      await loadTicket({ silent: true });
      showNotice(nextStatus === "resolved" ? "Ticket resolved successfully." : "Status updated.");
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update status.", "error");
    } finally {
      setUpdating(false);
    }
  };

  const resolved = ticket?.support_status === "resolved";
  const currentStatusAppearance = statusAppearance(ticket?.support_status);

  return (
    <DashboardLayout>
      <DashboardNavbar title="Support Reply" />
      <MDBox
        sx={{
          height: { xs: "calc(100dvh - 190px)", md: "calc(100dvh - 160px)" },
          minHeight: 0,
        }}
      >
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <MDBox
            px={{ xs: 2, md: 3 }}
            py={2}
            flexShrink={0}
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={1.5}
            sx={{ backgroundColor: "#0f1d35" }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <MDButton
                size="small"
                variant="outlined"
                onClick={() => navigate("/support-issues")}
                sx={{ color: "#fff !important", borderColor: "#7184a1 !important" }}
              >
                ← Tickets
              </MDButton>
              <MDBox>
                <MDTypography variant="h5" sx={{ color: "#ffffff", lineHeight: 1.2 }}>
                  {ticket?.ticket_id || "Support Ticket"}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#a9bad3" }}>
                  Support conversation and ticket activity
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <MDButton
                size="small"
                variant="outlined"
                onClick={syncConversation}
                disabled={!ticket || syncing || loading || sending || updating}
                sx={{ color: "#fff !important", borderColor: "#7184a1 !important" }}
              >
                {syncing ? "Refreshing..." : "↻ Refresh Messages"}
              </MDButton>
              {ticket ? (
                <MDBox
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: "34px",
                    px: 1.5,
                    borderRadius: "999px",
                    border: `1px solid ${currentStatusAppearance.border}`,
                    backgroundColor: currentStatusAppearance.background,
                    color: `${currentStatusAppearance.color} !important`,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusLabel(ticket.support_status)}
                </MDBox>
              ) : null}
            </MDBox>
          </MDBox>

          {loading ? (
            <MDBox flex={1} display="flex" alignItems="center" justifyContent="center">
              <MDTypography sx={{ color: "#475569" }}>Loading conversation...</MDTypography>
            </MDBox>
          ) : ticket ? (
            <MDBox
              flex={1}
              minHeight={0}
              p={{ xs: 1.5, md: 2.5 }}
              display="flex"
              flexDirection="column"
              sx={{ overflow: "hidden", backgroundColor: "#ffffff" }}
            >
              <Grid
                container
                spacing={2}
                mb={1.5}
                flexShrink={0}
                sx={{
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  p: { xs: 1, md: 1.5 },
                }}
              >
                <Grid item xs={12} md={8.5}>
                  <MDTypography variant="h6" sx={{ color: "#172b4d", mb: 0.5 }}>
                    {ticket.reason}
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "#334155", fontWeight: 600 }}>
                    {ticket.user_id?.name || ticket.name || "-"}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ color: "#64748b" }}>
                    User ID: {ticket.user_id?._id || ticket.user_id || "-"} · {ticket.user_id?.mobile || ticket.mobile || "No phone"} · {ticket.user_id?.email || ticket.email || "No email"}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ color: "#64748b", mt: 0.5 }}>
                    Raised {formatDate(ticket.createdAt)} · Last activity {formatDate(ticket.last_activity_at)}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3.5}>
                  <MDTypography variant="caption" display="block" sx={{ color: "#64748b", mb: 0.75, fontWeight: 700 }}>
                    Change ticket status
                  </MDTypography>
                  <MDBox
                    display="grid"
                    sx={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.75 }}
                  >
                    {statusOptions.map((option) => {
                      const active = ticket.support_status === option.value;
                      const appearance = statusAppearance(option.value);
                      return (
                        <MDBox
                          key={option.value}
                          component="button"
                          type="button"
                          disabled={resolved || updating || active}
                          onClick={() => changeStatus(option.value)}
                          sx={{
                            minHeight: "36px",
                            px: 1,
                            borderRadius: "9px",
                            border: `1px solid ${active ? appearance.border : "#d5dee9"}`,
                            backgroundColor: active ? appearance.background : "#ffffff",
                            color: `${active ? appearance.color : "#475569"} !important`,
                            fontFamily: "inherit",
                            fontSize: "0.72rem",
                            fontWeight: active ? 800 : 700,
                            cursor: resolved || updating || active ? "default" : "pointer",
                            opacity: resolved && !active ? 0.5 : 1,
                          }}
                        >
                          {active ? `✓ ${option.label}` : option.label}
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </Grid>
              </Grid>

              <MDBox mb={0.75} display="flex" justifyContent="space-between" alignItems="center" flexShrink={0}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#172b4d" }}>
                  Conversation
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                  {visibleMessages.length} message{visibleMessages.length === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>

              <MDBox
                ref={messagesRef}
                sx={{
                  flex: 1,
                  minHeight: "120px",
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  p: { xs: 1.25, md: 2 },
                }}
              >
                {visibleMessages.map((item) => {
                  const adminMessage = item.sender_type === "admin";
                  return (
                    <MDBox key={item._id} display="flex" justifyContent={adminMessage ? "flex-end" : "flex-start"} mb={1.5}>
                      <MDBox
                        sx={{
                          width: "fit-content",
                          maxWidth: { xs: "92%", md: "72%" },
                          backgroundColor: adminMessage ? "#e8f2ff" : "#ffffff",
                          border: `1px solid ${adminMessage ? "#bfdbfe" : "#dbe3ed"}`,
                          borderRadius: adminMessage ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                          boxShadow: "0 3px 10px rgba(15, 29, 53, 0.05)",
                          p: 1.5,
                        }}
                      >
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: adminMessage ? "#1d4ed8" : "#334155" }}>
                          {adminMessage ? "Support Team" : "User"}
                        </MDTypography>
                        {item.image?.url ? (
                          <a href={imageUrl(item.image.url)} target="_blank" rel="noreferrer">
                            <img
                              src={imageUrl(item.image.url)}
                              alt="Support attachment"
                              style={{ display: "block", maxWidth: "100%", maxHeight: "240px", borderRadius: "8px", marginTop: "8px" }}
                            />
                          </a>
                        ) : null}
                        {item.message ? (
                          <MDTypography variant="body2" mt={0.5} sx={{ color: "#172b4d", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.55 }}>
                            {item.message}
                          </MDTypography>
                        ) : null}
                        <MDTypography variant="caption" display="block" mt={0.75} sx={{ color: "#7c8ca5", textAlign: "right" }}>
                          {formatDate(item.createdAt)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  );
                })}
                {!visibleMessages.length ? (
                  <MDTypography variant="button" sx={{ color: "#64748b" }}>No messages found.</MDTypography>
                ) : null}
              </MDBox>

              {resolved ? (
                <MDBox mt={1.5} p={1.5} flexShrink={0} sx={{ background: "#edf9f0", borderRadius: "10px" }}>
                  <MDTypography variant="button" color="success">
                    ✓ Issue Resolved — further messages cannot be added.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox
                  mt={1.5}
                  p={{ xs: 1.25, md: 1.5 }}
                  flexShrink={0}
                  sx={{ border: "1px solid #dbe3ed", borderRadius: "14px", backgroundColor: "#f8fafc" }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#172b4d" }}>Reply to user</MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#64748b" }}>{reply.length}/2000</MDTypography>
                  </MDBox>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Type your reply here..."
                    value={reply}
                    inputProps={{ maxLength: 2000 }}
                    onChange={(event) => setReply(event.target.value)}
                    sx={lightFieldSx}
                  />
                  <MDBox mt={1} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                    <MDButton variant="outlined" color="info" component="label">
                      Attach Image
                      <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => selectReplyImage(event.target.files?.[0] || null)} />
                    </MDButton>
                    {replyImage ? <MDButton variant="text" color="error" onClick={() => setReplyImage(null)}>Remove</MDButton> : null}
                    {replyPreview ? <img src={replyPreview} alt="Reply preview" style={{ width: "52px", height: "40px", objectFit: "cover", borderRadius: "7px", border: "1px solid #dbe3ed" }} /> : null}
                    <MDTypography variant="caption" sx={{ color: "#64748b", flex: 1 }}>
                      {replyImage ? replyImage.name : "JPG, PNG or WEBP · Max 5 MB"}
                    </MDTypography>
                    <MDButton
                      color="info"
                      onClick={sendReply}
                      disabled={sending || (!reply.trim() && !replyImage)}
                    >
                      {sending ? "Sending..." : "Send Reply"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          ) : (
            <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5}>
              <MDTypography variant="h6" sx={{ color: "#172b4d" }}>Ticket could not be loaded.</MDTypography>
              <MDButton color="info" onClick={() => navigate("/support-issues")}>Back to Tickets</MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>

      <MDSnackbar
        color={noticeColor}
        icon="notifications"
        title="AgriQuiz"
        content={notice}
        dateTime="Just now"
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
      />
    </DashboardLayout>
  );
}

export default SupportTicketReply;
